import React from 'react';

import {
  Button,
  Card,
  CardActions as MuiCardActions,
  CardContent,
  Grid,
  Stack,
  styled,
  Typography,
  Box,
  Divider
} from '@mui/material';
import _ from 'lodash';
import {
  activityTemplates,
  AuthWrapper,
  fields,
  InvestorSearchResultCard,
  LoadingOverlay,
  SearchBar,
  enumCache,
  InvestmentAreaMultiSelectField,
  InvestmentCategorySelectField,
  InvestmentGeographyGroupedMultiSelectField,
  InvestmentStageMultiSelectField,
  USStateSelectField,
  RidgeVerifiedPopover,
  appEvents,
  ExternalLink,
  appStateUtil
} from 'modules';
import { useForm } from 'react-hook-form';

import { Masonry as MuiMasonry } from '@mui/lab';
import * as PaginationControl from './PaginationControl/PaginationControl';
import * as dataService from './dataService';



const CheckboxController = fields.genCheckboxControllerComponent<dataService.IFormData>();
const InvestmentCategorySelectFieldController = fields.genEnumSelectFieldControllerComponent<dataService.IFormData, enumCache.IInvestmentCategory>(InvestmentCategorySelectField({
  label: 'Category'
}));
const InvestmentStageMultiSelectFieldController = fields.genEnumMultiSelectFieldControllerComponent<dataService.IFormData, enumCache.IInvestmentStage>(InvestmentStageMultiSelectField({
  label: 'Company/Investment Stage'
}));
const InvestmentGeographyGroupedMultiSelectFieldController = fields.genGroupedEnumMultiSelectFieldControllerComponent<dataService.IFormData, enumCache.IInvestmentGeography>(InvestmentGeographyGroupedMultiSelectField({
  label: 'Investment Geography'
}));
const InvestmentAreaMultiSelectFieldController = fields.genEnumMultiSelectFieldControllerComponent<dataService.IFormData, string>(InvestmentAreaMultiSelectField({
  label: 'Company/Investment Area'
}));
const USStateSelectFieldController = fields.genEnumSelectFieldControllerComponent<dataService.IFormData, string>(USStateSelectField({
  label: 'State'
}));
const ControlledTextField = fields.genTextFieldControllerComponent<dataService.IFormData>();

const StyledStack = styled(Stack)(({ theme }) => ({
  textAlign: 'center',
  marginBottom: theme.spacing(5)
}));

const ResultsTypography = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(3)
}));

const Masonry = styled(MuiMasonry)({
  flexFlow: 'column wrap'
});

const OuterInvestorLocationGrid = styled(Grid)(({ theme }) => ({
  '&.MuiGrid-container': {
    marginTop: theme.spacing(1)
  }
}));

const InnerInvestorLocationGrid = styled(Grid)({
  justifyContent: 'space-between',
  '&.MuiGrid-container': {
    marginTop: 0
  }
});

const PartialWidthTextFieldGrid = styled(Grid)(({
  padding: 0
}));

const CardActions = styled(MuiCardActions)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  backgroundColor: '#F0F7FA',
  height: 80,
  padding: theme.spacing(2)
}));

const ClearAllButton = styled(Button)({
  color: '#528898',
  textTransform: 'none',
  fontWeight: 700
});

const DescriptionTypography = styled(Typography)(({ theme }) => ({
  fontSize: 20,
  color: '#486c82'
}));

const PaginationControlsBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  marginBottom: 106
}));



export interface IInvestorsActivityProps { }

export const InvestorsActivity: React.FC<IInvestorsActivityProps> = props => {
  const {
    queryTuple,
    pageInfo,
    fnQuery,
    fnQueryPage
  } = dataService.useInvestorsActivity();

  const investorSearchFilters = appStateUtil.getAppState().investorSearchFilters;

  const form = useForm<dataService.IFormData>({
    defaultValues: {
      isFavorited: false,
      canLeadInvestments: false,
      verifiedByRidge: false,
      city: '',
      usState: '',
      country: ''
    }
  });

  const [currentSearchText, setCurrentSearchText] = React.useState('');
  const formState = form.watch();



  React.useEffect(() => {
    form.setValue('isFavorited', investorSearchFilters.isFavorited);
    form.setValue('canLeadInvestments', investorSearchFilters.canLeadInvestments);
    form.setValue('verifiedByRidge', investorSearchFilters.verifiedByRidge);
    form.setValue('investmentCategories', investorSearchFilters.investmentCategories);
    form.setValue('investmentAreas', investorSearchFilters.investmentAreas);
    form.setValue('investmentStages', investorSearchFilters.investmentStages);
    form.setValue('investmentGeographies', investorSearchFilters.investmentGeographies);
    form.setValue('city', investorSearchFilters.city);
    form.setValue('usState', investorSearchFilters.usState);
    form.setValue('country', investorSearchFilters.country);
  }, []);



  function handleFavoriteError(investorName: string) {
    appEvents.broadcast({
      type: 'modal',
      args: {
        type: 'notify',
        title: 'Error',
        body: `Unable to favorite ${investorName}.`
      }
    });
  }



  async function processSubmit(data: dataService.IFormData) {
    appStateUtil.setInvestorSearchFilters({
      isFavorited: data.isFavorited,
      canLeadInvestments: data.canLeadInvestments,
      verifiedByRidge: data.verifiedByRidge,
      investmentCategories: data.investmentCategories,
      investmentAreas: data.investmentAreas,
      investmentStages: data.investmentStages,
      investmentGeographies: data.investmentGeographies,
      city: data.city,
      usState: data.usState,
      country: data.country
    });

    fnQuery({
      filter: {
        ...data,
        searchText: currentSearchText
      }
    });
  }



  const handleChangePage = React.useCallback((pageIndex: number) => {
    fnQueryPage(pageIndex);
  }, [fnQueryPage]);



  return (
    <AuthWrapper.FC>
      <activityTemplates.BaseActivityTemplate.FC>
        {!currentSearchText && (
          <LoadingOverlay active={queryTuple.loading} />
        )}

        <StyledStack
          spacing={2}>
          <Typography variant="h1">
            Investor Search
          </Typography>

          <Stack>
            <DescriptionTypography>
              Discover and favorite investors for your fundraise process. Favorited investors will be added to the My Fundraise tool.
            </DescriptionTypography>
            <DescriptionTypography>
              Investment area filtering is thesis-specific. Please email&nbsp;
              <ExternalLink.Text
                color="#486C82"
                href="mailto:support@raisewithridge.com">
                support@raisewithridge.com
              </ExternalLink.Text>
              &nbsp;for investor suggestions or improvements. <strong>New investors added daily!</strong>
            </DescriptionTypography>
          </Stack>
        </StyledStack>

        <Grid
          container
          spacing={3}>
          {/* Filters */}
          <Grid item xs={3}>
            <Card variant="outlined">
              <form onSubmit={form.handleSubmit(processSubmit)}>
                <CardContent>
                  <Typography variant="h2">Filter By</Typography>

                  <Stack spacing={4} mt={2}>
                    <Stack>
                      <CheckboxController
                        fieldKey="isFavorited"
                        label="Show Only Favorites"
                        formControl={form.control}
                        isDisabled={queryTuple.loading} />

                      <CheckboxController
                        fieldKey="canLeadInvestments"
                        label="Lead"
                        formControl={form.control}
                        isDisabled={queryTuple.loading} />

                      <Box>
                        <CheckboxController
                          fieldKey="verifiedByRidge"
                          label="Ridge Verified"
                          formControl={form.control}
                          isDisabled={queryTuple.loading} />
                        <RidgeVerifiedPopover.FC />
                      </Box>
                    </Stack>

                    <InvestmentCategorySelectFieldController
                      hideOptions={['startups']}
                      fieldKey="investmentCategories"
                      formControl={form.control}
                      placeholder="Select an option"
                      isDisabled={queryTuple.loading} />

                    <InvestmentAreaMultiSelectFieldController
                      fieldKey="investmentAreas"
                      formControl={form.control}
                      placeholder="Select an option"
                      isDisabled={queryTuple.loading} />

                    <InvestmentStageMultiSelectFieldController
                      fieldKey="investmentStages"
                      formControl={form.control}
                      placeholder="Select an option"
                      isDisabled={queryTuple.loading} />

                    <InvestmentGeographyGroupedMultiSelectFieldController
                      fieldKey="investmentGeographies"
                      formControl={form.control}
                      placeholder="Select an option"
                      isDisabled={queryTuple.loading} />

                    <Divider />

                    <Typography color="#1E3949" fontSize={14} fontWeight={700}>
                      Investor Location
                    </Typography>

                    <OuterInvestorLocationGrid container mt={1}>
                      <InnerInvestorLocationGrid container item>
                        <PartialWidthTextFieldGrid item xs={6}>
                          <ControlledTextField
                            fieldKey="city"
                            label="City"
                            formControl={form.control}
                            isDisabled={queryTuple.loading} />
                        </PartialWidthTextFieldGrid>

                        <PartialWidthTextFieldGrid item xs={5}>
                          <USStateSelectFieldController
                            fieldKey="usState"
                            formControl={form.control}
                            isDisabled={queryTuple.loading} />
                        </PartialWidthTextFieldGrid>
                      </InnerInvestorLocationGrid>

                      <Grid item xs={12}>
                        <ControlledTextField
                          fieldKey="country"
                          label="Country"
                          formControl={form.control}
                          isDisabled={queryTuple.loading} />
                      </Grid>
                    </OuterInvestorLocationGrid>
                  </Stack>

                </CardContent>

                <CardActions>
                  <ClearAllButton
                    variant="text"
                    type="submit"
                    size="large"
                    onClick={() => {
                      form.reset();

                      appStateUtil.setInvestorSearchFilters({
                        isFavorited: undefined,
                        canLeadInvestments: undefined,
                        verifiedByRidge: undefined,
                        investmentCategories: undefined,
                        investmentAreas: undefined,
                        investmentStages: undefined,
                        investmentGeographies: undefined,
                        city: undefined,
                        usState: undefined,
                        country: undefined
                      });
                    }}>
                    Clear All
                  </ClearAllButton>

                  <Button type="submit">
                    Apply Filters
                  </Button>
                </CardActions>
              </form>
            </Card>
          </Grid>

          {/* Results */}
          <Grid item xs={9}>

            <SearchBar.FC onChange={searchText => {
              setCurrentSearchText(searchText);
              fnQuery({
                filter: {
                  ...formState,
                  searchText
                }
              });
            }} />

            <ResultsTypography variant="h3">
              {pageInfo.totalRows} results
            </ResultsTypography>

            <Masonry
              columns={3}
              spacing={3}>
              {!queryTuple.loading && _.map(queryTuple.data.results, result => {
                return (
                  <InvestorSearchResultCard.FC
                    key={result.id}
                    userId={result.id}
                    logoUrl={result.logoUrl}
                    title={result.name}
                    description={result.description}
                    websiteUrl={result.websiteUrl}
                    isFavorited={result.isFavorited}
                    onClickFavorite={() => fnQuery({ filter: formState })}
                    onFavoriteError={() => handleFavoriteError(result.name)} />
                );
              })}
            </Masonry>

            <PaginationControlsBox>
              <PaginationControl.FC
                pageInfo={pageInfo}
                onChangePage={handleChangePage} />
            </PaginationControlsBox>
          </Grid>
        </Grid>
      </activityTemplates.BaseActivityTemplate.FC>
    </AuthWrapper.FC>
  );
};
